/**
 * debate.ts — Firestore helpers for real-time debate subscriptions.
 *
 * The backend writes all debate progress to `debates/{debateId}`.
 * The frontend subscribes here for live updates (typewriter effect, audio chunks, etc.).
 */

import {
  doc,
  collection,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "./config";
import { authHeaders } from "./authHeaders";
import { AnswerFormat, Verdict, parseAnswerFormat, parseVerdict } from "../utils/verdict";
import { DebateSource, parseSources } from "../utils/sources";

// ── Types ───────────────────────────────────────────────────────────────────

export interface DebateDoc {
  uid: string | null;
  question: string;
  experts: string[];
  plan: string;
  audio: boolean;
  concise: boolean;
  debateMode?: "single" | "multi";
  status: "running" | "completed" | "error";
  steps: any[];
  chaosSteps: any[];
  finalAggregate: string | null;
  facilitatorDone: boolean;
  /** Multi-expert debates on questions with fixed answers. */
  answerFormat?: AnswerFormat | null;
  verdict?: Verdict | null;
  /** Links the experts relied on; steps refer to them by id. */
  sources?: DebateSource[];
  error: string | null;
  createdAt: any;
  updatedAt: any;
}

// ── Start a Firestore-backed debate via the backend ─────────────────────────

const START_DEBATE_URL =
  "https://startdebate-mt62q3wpaa-uc.a.run.app/startDebate";

export async function requestStartDebate(params: {
  question: string;
  experts: string[];
  plan: string;
  concise: boolean;
  audio: boolean;
  uid: string | null;
  email?: string | null;
  images?: string[]; // base64 data URIs (max 2, compressed)
  debateMode?: "single" | "multi";
  /** From setup; the server classifies the question itself when these are missing. */
  answerFormat?: AnswerFormat;
  category?: string;
}): Promise<string> {
  // Paid tiers and weekly limits are charged server-side to the token's uid.
  const resp = await fetch(START_DEBATE_URL, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });

  if (!resp.ok) {
    const errBody = await resp.text();
    if (resp.status === 401 || resp.status === 402) {
      const err = new Error("insufficient_debate_credits") as any;
      err.code = "insufficient_debate_credits";
      throw err;
    }
    if (resp.status === 429) {
      // Body is { error: "free_limit" | "audio_limit" }.
      const code = (() => {
        try {
          return JSON.parse(errBody).error;
        } catch {
          return null;
        }
      })();
      const err = new Error("weekly_limit_reached") as any;
      err.code = code === "audio_limit" ? "audio_limit" : "free_limit";
      throw err;
    }
    throw new Error(`startDebate failed: ${resp.status} — ${errBody}`);
  }

  // The backend streams NDJSON. The first line contains { debateId }.
  // We read just that first line and return immediately.
  // The HTTP connection stays open (keeping the Cloud Function alive)
  // but we don't need to consume the rest — Firestore handles updates.
  const reader = resp.body?.getReader();
  if (!reader) {
    throw new Error("startDebate response has no body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const newlineIdx = buffer.indexOf("\n");
    if (newlineIdx >= 0) {
      const firstLine = buffer.slice(0, newlineIdx).trim();
      // Release the reader — we don't need to consume keep-alive pings.
      // The backend's HTTP connection stays alive regardless.
      reader.releaseLock();
      const data = JSON.parse(firstLine);
      if (!data.debateId) {
        throw new Error("startDebate response missing debateId");
      }
      return data.debateId;
    }
  }

  throw new Error("startDebate: stream ended before receiving debateId");
}

/**
 * Watches a debate reopened from history. `onUnavailable` fires when the doc is
 * gone or can't be read, which Firestore rules report the same way.
 */
export function onSavedDebateSnapshot(
  debateId: string,
  onDebate: (debate: DebateDoc) => void,
  onUnavailable: () => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, "debates", debateId),
    (snap) => (snap.exists() ? onDebate(snap.data() as DebateDoc) : onUnavailable()),
    (error) => {
      console.error(`[onSavedDebateSnapshot] debates/${debateId}:`, error.code, error.message);
      onUnavailable();
    },
  );
}

/** Every saved audio event of a finished debate, in playing order. */
export async function loadDebateAudioChunks(debateId: string): Promise<any[]> {
  const snap = await getDocs(
    query(collection(db, "debates", debateId, "audioChunks"), orderBy("seq", "asc")),
  );
  return snap.docs.map((d) => d.data());
}

// ── Subscribe to live debate updates ────────────────────────────────────────

export interface DebateCallbacks {
  onSteps: (steps: any[]) => void;
  onChaos: (chaosSteps: any[]) => void;
  onFinal: (finalAggregate: string | null) => void;
  /** The panel verdict (once every expert has revised) and the answer format it reads against. */
  onVerdict: (verdict: Verdict | null, answerFormat: AnswerFormat | null) => void;
  onSources: (sources: DebateSource[]) => void;
  onFacilitatorDone: (done: boolean) => void;
  onAudioChunk: (chunks: any[]) => void;
  onStatus: (status: "running" | "completed" | "error") => void;
  onError: (error: string | null) => void;
}

/**
 * Subscribe to a debate doc's real-time updates from Firestore.
 * Audio chunks live in a subcollection `debates/{debateId}/audioChunks`
 * to avoid the 1MB document size limit.
 * Returns an unsubscribe function that cleans up both listeners.
 */
export function onDebateSnapshot(
  debateId: string,
  callbacks: DebateCallbacks,
): Unsubscribe {
  const ref = doc(db, "debates", debateId);

  // ── Main debate doc listener (steps, chaos, final, status) ──────────
  const unsubMain = onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        console.warn(
          `[onDebateSnapshot] doc debates/${debateId} does not exist yet`,
        );
        return;
      }
      const data = snap.data() as DebateDoc;

      // Steps & chaos — full replace (the backend merges in-place)
      callbacks.onSteps(data.steps || []);
      callbacks.onChaos(data.chaosSteps || []);

      // Final aggregate
      callbacks.onFinal(data.finalAggregate || null);
      callbacks.onVerdict(parseVerdict(data.verdict), parseAnswerFormat(data.answerFormat));
      callbacks.onSources(parseSources(data.sources));
      callbacks.onFacilitatorDone(data.facilitatorDone === true);

      // Status and error
      callbacks.onStatus(data.status);
      if (data.error) {
        callbacks.onError(data.error);
      }
    },
    (error) => {
      console.error(
        `[onDebateSnapshot] Firestore listener error for debates/${debateId}:`,
        error.code,
        error.message,
      );
      callbacks.onError(
        `Firestore read denied: ${error.code} — ${error.message}`,
      );
    },
  );

  // ── Audio subcollection listener ────────────────────────────────────
  // Each document is one audio event, ordered by `seq` field.
  // We track which seq numbers we've already delivered to avoid duplicates.
  let lastDeliveredSeq = -1;

  const audioQuery = query(
    collection(db, "debates", debateId, "audioChunks"),
    orderBy("seq", "asc"),
  );

  const unsubAudio = onSnapshot(
    audioQuery,
    (snapshot) => {
      const newChunks: any[] = [];
      for (const change of snapshot.docChanges()) {
        if (change.type === "added") {
          const data = change.doc.data();
          // Only deliver chunks we haven't seen yet
          if (typeof data.seq === "number" && data.seq > lastDeliveredSeq) {
            newChunks.push(data);
          }
        }
      }
      if (newChunks.length > 0) {
        // Sort by seq to ensure correct order
        newChunks.sort((a, b) => a.seq - b.seq);
        lastDeliveredSeq = newChunks[newChunks.length - 1].seq;
        callbacks.onAudioChunk(newChunks);
      }
    },
    (error) => {
      console.error(
        `[onDebateSnapshot] Audio subcollection listener error for debates/${debateId}:`,
        error.code,
        error.message,
      );
    },
  );

  // Return a combined unsubscribe function
  return () => {
    unsubMain();
    unsubAudio();
  };
}
